import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { PerformanceTile } from "../../../PerformanceTile/PerformanceTile";
import { PerformanceTileHeader } from "../../../PerformanceTile/PerformanceTileHeader/PerformanceTileHeader";
import { LegsProgress, type LegsProgressItem } from "../../../PerformanceTile/LegsProgress/LegsProgress";
import { PVRequirementTile } from "../../../PerformanceTile/PVRequirementTile/PVRequirementTile";
import { PinRequirement } from "../../../PerformanceTile/PinRequirement/PinRequirement";
import { MetricCard } from "../../../PerformanceTile/MetricCard/MetricCard";
import { KpiTrackers } from "../../../PerformanceTile/KpiTrackers/KpiTrackers";
import { AwardProgressBadge } from "../../../../../../common/components/AwardProgressBadge/AwardProgressBadge";
import { IconTurnDownLeftArrow } from "../../../../../../common/icons/IconTurnDownLeftArrow/IconTurnDownLeftArrow";
import { AWARD_PROGRESS } from "../../../../../../common/enums/award-progress-codes";
import defaultAwardContent from "../../../../../../content/awardLabels";
import type { TileHeader } from "../../IncomeBreakdown.types";

export interface IncomeTileContentProps {
  header?: TileHeader;
  legsProgress?: { items: LegsProgressItem[] };
  linkRows?: { items: { label: string; link: string; value: string; disabled?: boolean }[] };
  kpiTrackers?: {
    items: {
      label: ReactNode;
      percentage?: number;
      achievedSegments?: number;
      totalSegments?: number;
      isMet?: boolean;
      variant?: "progress" | "legs";
    }[];
  };
  pvRequirement?: { percent: number; met: boolean; label: string };
  pvRequirements?: { percent: number; met: boolean; label: string }[];
  metricCard?: { items: { label: string; value: string }[] };
  pinRequirement?: { met: boolean; label: string; value?: string; statusTag?: AWARD_PROGRESS; status: AWARD_PROGRESS };
  pinRequirements?: {
    met: boolean;
    label: string;
    value?: string;
    statusTag?: AWARD_PROGRESS;
    status: AWARD_PROGRESS;
  }[];
  legProgressKPI?: {
    items: {
      label: ReactNode;
      achievedSegments: number;
      totalSegments: number;
      isMet: boolean;
    }[];
  };
  statusBadge?: AWARD_PROGRESS;
  statusMessage?: string;
  comingSoon?: { title: string; message: string };
}

export const IncomeTileContent = ({
  header,
  legsProgress,
  linkRows,
  kpiTrackers,
  pvRequirement,
  pvRequirements,
  metricCard,
  pinRequirement,
  pinRequirements,
  legProgressKPI,
  statusBadge,
  statusMessage,
  comingSoon,
}: IncomeTileContentProps) => {
  const { t } = useTranslation(["awardLabels"]);
  const noDataLabel = t(
    "awardLabels.no-data-available",
    defaultAwardContent.awardLabels["no-data-available"] as string,
  );

  const showLegsDivider = !!(
    kpiTrackers ||
    pvRequirement ||
    pvRequirements ||
    metricCard ||
    pinRequirement ||
    pinRequirements ||
    legProgressKPI ||
    statusBadge
  );

  return (
    <PerformanceTile>
      {header && (
        <PerformanceTileHeader
          label={header.label}
          value={header.value}
          link={header.link}
          labelLink={header.labelLink}
          tag={header.tag}
          showArrow={header.showArrow}
          tealiumTag={header.tealiumTag}
        />
      )}
      {comingSoon && (
        <div className="income-breakdown__coming-soon">
          <p className="income-breakdown__coming-soon-title">{comingSoon.title}</p>
          <p className="income-breakdown__coming-soon-message">{comingSoon.message}</p>
        </div>
      )}
      {legsProgress && <LegsProgress {...legsProgress} showDivider={showLegsDivider} />}
      {statusMessage && <p className="AnnualPerformanceIncome__tileSubMessage">{statusMessage}</p>}
      {linkRows && (
        <div className="income-breakdown__link-rows">
          {linkRows.items.map((item, i) => (
            <div key={i} className="income-breakdown__link-row">
              {item.disabled ? (
                <span
                  aria-disabled="true"
                  className="income-breakdown__link-row-label income-breakdown__link-row-label--disabled"
                >
                  {item.label}
                </span>
              ) : (
                <a
                  href={item.link}
                  className="income-breakdown__link-row-label"
                  data-testid={`income-breakdown-link-${item.link.slice(1)}`}
                >
                  {item.label}
                </a>
              )}
              <span
                className="income-breakdown__link-row-value"
                {...(item.value === "--" ? { role: "text", "aria-label": noDataLabel } : {})}
              >
                {item.value === "--" ? <span aria-hidden="true">{item.value}</span> : item.value}{" "}
                <IconTurnDownLeftArrow />
              </span>
            </div>
          ))}
        </div>
      )}
      {kpiTrackers && <KpiTrackers items={kpiTrackers.items} />}
      {pvRequirement && <PVRequirementTile {...pvRequirement} />}
      {pvRequirements && pvRequirements.map((p, i) => <PVRequirementTile key={i} {...p} />)}
      {metricCard && <MetricCard {...metricCard} />}
      {pinRequirement && <PinRequirement {...pinRequirement} />}
      {pinRequirements && pinRequirements.map((p, i) => <PinRequirement key={i} {...p} />)}
      {legProgressKPI && (
        <KpiTrackers
          items={legProgressKPI.items.map((item) => ({ ...item, variant: "legs" as const, percentage: 0 }))}
        />
      )}
      {statusBadge && (
        <div className="income-breakdown__status-tag">
          <AwardProgressBadge status={statusBadge} hideIcon />
        </div>
      )}
    </PerformanceTile>
  );
};
